/**
 * Placeholder-body census (W42 owner report, 2026-10-14): the capture tool's
 * reliable signal that a character on screen is still the fallback body.
 *
 * Every character starts as a placeholder built in `Game.buildEntityMesh` — a
 * player is a plain blue capsule + nose cone, an enemy a green cone + eye — and is
 * upgraded IN PLACE once its model loads (rig-avatar / creatures renderers remove
 * the placeholder children from the entity group and add the real model). If the
 * load is still in flight, has fallen back (no pack, load error), or the avatar was
 * rebuilt by a fresh session ack, the placeholder is what the frame draws.
 *
 * The capture gate used to infer this from bookkeeping (`ownRigged` = "a rig root
 * was once recorded", `playerRigs*` = upgrade records). Bookkeeping can lie: the
 * W42 `x--w42-dungeon-clears-pay` still shipped the own player as a bare capsule
 * while every flag read ready — a session ack rebuilt the avatar mid-hold and
 * `ownRigRoot` was never cleared, and a capsule FALLBACK drops its record so it
 * never counted as "not settled". This census asks the scene itself: which
 * placeholder meshes are attached and visible right now.
 *
 * The placeholder meshes are tagged at build time with `userData[PLACEHOLDER_BODY_KEY]`;
 * the census walks the scene's VISIBLE subtree (an invisible ancestor hides it, a
 * detached group is not reached) and counts entity groups — not meshes — by kind.
 */

import * as THREE from 'three';

/** `userData` key on each placeholder body mesh; the value is its {@link PlaceholderKind}. */
export const PLACEHOLDER_BODY_KEY = 'placeholderBody';

/** Which fallback body a mesh belongs to. */
export type PlaceholderKind = 'player' | 'enemy';

/** Tag a fallback-body mesh so the census can find it. */
export function markPlaceholderBody<T extends THREE.Object3D>(mesh: T, kind: PlaceholderKind): T {
  mesh.userData[PLACEHOLDER_BODY_KEY] = kind;
  return mesh;
}

/** Visible placeholder bodies, counted per entity group. */
export interface PlaceholderCounts {
  /** The own avatar is drawn as the capsule (0 or 1). */
  own: number;
  /** Other players drawn as the capsule. */
  players: number;
  /** Enemies drawn as the placeholder cone. */
  enemies: number;
  /** Of all the above, how many sit inside the camera frustum right now. */
  inFrame: number;
}

/** Radius (m) around a body's centre used for the frustum test — a capsule is ~1.7 m tall. */
const BODY_RADIUS = 1.2;
/** Height (m) of a body's centre above its group origin. */
const BODY_CENTRE_Y = 0.85;

/**
 * Count the placeholder bodies `root` currently draws. `own` is the own avatar's
 * entity group (or null); `camera`, when given, also fills `inFrame`.
 */
export function countPlaceholderBodies(
  root: THREE.Object3D,
  own: THREE.Object3D | null,
  camera?: THREE.Camera,
): PlaceholderCounts {
  const groups = new Map<THREE.Object3D, PlaceholderKind>();
  root.traverseVisible((obj) => {
    const kind = obj.userData[PLACEHOLDER_BODY_KEY] as PlaceholderKind | undefined;
    if (kind === undefined) return;
    groups.set(obj.parent ?? obj, kind);
  });
  const counts: PlaceholderCounts = { own: 0, players: 0, enemies: 0, inFrame: 0 };
  let frustum: THREE.Frustum | null = null;
  if (camera !== undefined && groups.size > 0) {
    camera.updateMatrixWorld();
    frustum = new THREE.Frustum().setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
    );
  }
  const centre = new THREE.Vector3();
  const sphere = new THREE.Sphere(centre, BODY_RADIUS);
  for (const [group, kind] of groups) {
    if (group === own) counts.own += 1;
    else if (kind === 'player') counts.players += 1;
    else counts.enemies += 1;
    if (frustum !== null) {
      group.updateWorldMatrix(true, false);
      group.getWorldPosition(centre);
      centre.y += BODY_CENTRE_Y;
      if (frustum.intersectsSphere(sphere)) counts.inFrame += 1;
    }
  }
  return counts;
}

/** The rig renderer's own bookkeeping (`PlayerRigRenderer.stats()`). */
export interface RigStats {
  playerRigsTracked: number;
  playerRigsSettled: number;
}

/**
 * The capture-readiness fields of the dev scene dump, with the census folded in.
 *
 * CloudMediaStudio's gate (`clip_capture.py` `_world_ready_js`) already REQUIRES
 * `ownRigged` and `playerRigsSettled >= playerRigsTracked` whenever the fields are
 * present, before the hold and on every ~1 s sample during it, and fails + retakes
 * the clip on a lapse. Folding the census into those same fields makes that gate
 * see the scene's truth with no studio change:
 *   - `ownRigged` is true only when a rig was recorded AND no own capsule is drawn;
 *   - a remote capsule — in flight OR a permanent fallback, which drops its rig
 *     record — always counts as one tracked-but-unsettled player.
 * `placeholderBodies` (incl. enemy cones and the in-frame count) and `capsuleFree`
 * are reported as well, for any consumer that wants the census directly.
 */
export function characterReadiness(
  ownRigRecorded: boolean,
  rigStats: RigStats | null,
  counts: PlaceholderCounts,
): {
  ownRigged: boolean;
  playerRigsTracked?: number;
  playerRigsSettled?: number;
  placeholderBodies: PlaceholderCounts;
  capsuleFree: boolean;
} {
  const capsuleFree = counts.own === 0 && counts.players === 0;
  return {
    ownRigged: ownRigRecorded && counts.own === 0,
    ...(rigStats
      ? {
          playerRigsTracked: Math.max(
            rigStats.playerRigsTracked,
            rigStats.playerRigsSettled + counts.players,
          ),
          playerRigsSettled: rigStats.playerRigsSettled,
        }
      : counts.players > 0
        ? { playerRigsTracked: counts.players, playerRigsSettled: 0 }
        : {}),
    placeholderBodies: counts,
    capsuleFree,
  };
}
